import { For, JSX, JSXElement, ParentProps, createEffect, createMemo, createSignal, splitProps } from 'solid-js'
import { createStore } from 'solid-js/store';
import { debounce } from 'common';
import styles from './st-async-list.module.css'

type HTMLAttributes = Omit<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;
class AsyncListProps<T> {
    totalRows: number = 0;
    rowCount: number = 0;
    loadData: (page: number) => Promise<T[]> = () => Promise.resolve<T[]>([]);
    children: (row: T | undefined) => JSXElement = () => '';
    key?: string; // When key changes, the list will clear its cache and reload
}

export default function AsyncList<T>(props: AsyncListProps<T> & HTMLAttributes) {
    const ROW_HEIGHT = 30; // virtual row height for calculations
    const PAGE_SIZE = 10;

    const [local, htmlProps] = splitProps(props, [
        'totalRows',
        'rowCount',
        'loadData',
        'children',
        'class',
        'key',
    ]);

    const classList = createMemo(() => {
        const res = [styles.grid];
        if (local.class) {
            res.push(local.class);
        }
        return res.join(' ');
    });

    const [scrollTop, setScrollTop] = createSignal(0);
    const [dataCache, setDataCache] = createStore<Record<number, T>>({});
    const rowData = createMemo(() => {
        const startRow = Math.floor(scrollTop() / ROW_HEIGHT);
        const available = Math.max(0, local.totalRows - startRow);
        const count = Math.min(local.rowCount, available);
        return Array.from({ length: count }, (_, i) => dataCache[startRow + i]);
    });

    const fillRows = debounce(async (startRow: number) => {
        let nextStart = startRow + local.rowCount;

        do {
            var pageNum = Math.floor(startRow / PAGE_SIZE);
            var pageStart = pageNum * PAGE_SIZE;

            // check whether any index in this page is missing,
            // instead of checking `pageStart in dataCache` which only checks a single index.
            let needLoad = false;
            for (let i = 0; i < PAGE_SIZE; i++) {
                const idx = pageStart + i;
                if (idx >= local.totalRows) {
                    break;
                }

                if (!(idx in dataCache)) {
                    needLoad = true;
                    break;
                }
            }

            if (needLoad) {
                const data = await local.loadData(pageNum);
                const base = pageNum * PAGE_SIZE;
                const entries: [number, T][] = data.map((row, i) => [base + i, row]);

                setDataCache(prev => ({ ...prev, ...Object.fromEntries(entries) }));
            }

            startRow += PAGE_SIZE;
        } while (pageStart < local.totalRows && pageStart < nextStart);
    }, 250);

    createEffect(() => {
        const startRow = Math.floor(scrollTop() / ROW_HEIGHT);
        fillRows(startRow);
    });

    // Clear cache and reset scroll when key changes
    createEffect(() => {
        local.key; // Track key changes
        setDataCache({});
        setScrollTop(0);
    });

    function onScroll(e: Event) {
        const target = e.target as HTMLDivElement;
        setScrollTop(target.scrollTop);
    }

    return (
        <div class={styles.container} onScroll={onScroll}>
            <div class={styles.spacer} role="presentation" style={{ height: `${props.totalRows * ROW_HEIGHT}px` }}>&nbsp;</div>
            <div {...htmlProps} class={classList()} tabindex="0" role="listbox">
                <For each={rowData()}>{props.children}</For>
            </div>
        </div>
    );
}

AsyncList.Item = function Item(props: ParentProps) {
    return <div role="listitem">{props.children}</div>
};

AsyncList.defaultProps = new AsyncListProps<string>();